import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FormattedDateInput from '../../../components/ui/FormattedDateInput';
import WizardSelect from '../../../components/wizard/WizardSelect';
import { useAttorneyGeneralWizardContext } from '../../../hooks/useCaseRequestWizard';
import { isTodayOrFutureIsoDate } from '../../../utils/caseFieldValidation';
import { validationDateFutureOnly } from '../../../utils/validationI18n';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors } from '../../../utils/requiredValidation';
import { SectionCard } from '../WizardComponents';
import { AttorneyGeneralWizardActions } from './AttorneyGeneralWizardActions';
import { attorneyGeneralWizardPath, AttorneyGeneralWizardShell, FieldError, inpClass } from './AttorneyGeneralWizardShell';
import { isHighUrgencyLevel } from './attorneyGeneralHelpers';
import { agEnterPlaceholder, agSelectPlaceholder } from './attorneyGeneralPlaceholders';
import { minDeadlineDateForAttorneyGeneral, validateAttorneyGeneralStep } from './attorneyGeneralStepValidation';
import { useAttorneyGeneralLookups } from './useAttorneyGeneralLookups';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

const MAX_TEXT = 2000;

export default function AttorneyGeneralStep10TimelinePage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useAttorneyGeneralWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const { urgencyLevels } = useAttorneyGeneralLookups('step10');
  const minDeadlineDate = useMemo(() => minDeadlineDateForAttorneyGeneral(), []);
  const urgencyJustificationRequired = isHighUrgencyLevel(draft.urgencyLevelName);

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);

  const validate = (): boolean => {
    const { valid, errors: nextErrors, specs } = validateAttorneyGeneralStep(10, draft);
    setErrors(nextErrors);
    if (!valid) focusFirstInvalid(specs, nextErrors);
    return valid;
  };

  return (
    <AttorneyGeneralWizardShell currentStep={10} draft={draft}>
      <SectionCard title={f.sectionTimeline}>
        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>{f.urgencyLevel}</label>
            <WizardSelect
              id="ag-urgency"
              hasError={Boolean(errors.urgencyLevelId)}
              value={draft.urgencyLevelId}
              placeholder={f.urgencyLevel}
              options={urgencyLevels.map((u) => ({ id: u.id, label: u.name }))}
              onChange={(id) => {
                const name = urgencyLevels.find((u) => u.id === id)?.name ?? '';
                setDraft((d) => ({
                  ...d,
                  urgencyLevelId: id,
                  urgencyLevelName: name }));
                if (!isHighUrgencyLevel(name)) clearErr('urgencyJustification');
                clearErr('urgencyLevelId');
              }}
            />
            <FieldError message={errors.urgencyLevelId} />
          </div>
          <div className="ccr-fg">
            <label>{f.deadlineIfAny}</label>
            <FormattedDateInput
              id="ag-deadline"
              className={inpClass(Boolean(errors.deadline))}
              value={draft.deadline}
              minDate={minDeadlineDate}
              enableYmdViews
              placeholder={f.deadlineIfAny}
              onChange={(next) => {
                setDraft((d) => ({ ...d, deadline: next }));
                clearErr('deadline');
                if (next && !isTodayOrFutureIsoDate(next)) {
                  setErrors((e) => ({ ...e, deadline: validationDateFutureOnly() }));
                }
              }}
            />
            <FieldError message={errors.deadline} />
          </div>
        </div>
        <div className="ccr-fg" style={{ marginBottom: 0 }}>
          <label>
            {f.urgencyJustification} {urgencyJustificationRequired ? <span className="req">*</span> : null}
          </label>
          <textarea
            id="ag-urgencyJustification"
            className={inpClass(Boolean(errors.urgencyJustification))}
            rows={3}
            value={draft.urgencyJustification}
            onChange={(e) => {
              setDraft((d) => ({ ...d, urgencyJustification: maxLen('urgencyJustification', e.target.value, MAX_TEXT) }));
            }}
            placeholder={f.urgencyJustificationReason}
          />
          <FieldError message={errors.urgencyJustification} />
        </div>
      </SectionCard>

      <AttorneyGeneralWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={10}
        onBack={() => nav(attorneyGeneralWizardPath(9, draft.draftRequestId))}
        onNext={() => {
          if (validate()) nav(attorneyGeneralWizardPath(11, draft.draftRequestId));
        }}
        validateBeforeSave={validate}
      />
    </AttorneyGeneralWizardShell>
  );
}
